import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Checkbox, 
    FormControlLabel, 
    FormGroup, 
    Paper,
    Typography,
    Box,
    Grid
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useState, useMemo } from "react";

import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "../../components/TopNavBar/DefaultNavBar";
import GapSlider from "../../components/GapSlider";
import AssetCard from "../../components/AssetCard";

import AssetData from "../../assets/assetJson/assets.json";

// Images
import phantomNodeImg from "../../assets/images/phantom_node.png";
import spectraFlexImg from "../../assets/images/assetImages/SpectraFlex Adaptive Wear.png";
import morphoGloveImg from "../../assets/images/assetImages/Morpho-Touch Glove.png";
import missionScopeImg from "../../assets/images/assetImages/MissionScope Planner.png";
import swarmImg from "../../assets/images/assetImages/Swarm Intelligence Deployment Suite.png";
import advTactMobileImg from "../../assets/images/assetImages/Advanced Tactical Mobility System.png";
import stingrayImg from "../../assets/images/assetImages/STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB).png";
import disruptionImg from "../../assets/images/assetImages/Cyber-Physical Disruption Suite (CPDS).png";
import seopImg from "../../assets/images/assetImages/Strategic Effects Orchestration Platform (SEOP).png";
import agsfsImg from "../../assets/images/assetImages/Adaptive Ground Sensing & Fusion System (AGS-FS).png";
import tgtihImg from "../../assets/images/assetImages/Tactical Geospatial & Threat Intelligence Hub (TG-TIH).png";
import iideImg from "../../assets/images/assetImages/Intelligent Information Distribution Engine (IIDE).png";
import sddcmImg from "../../assets/images/assetImages/Secure Device & Data Concealment Module (SDDCM).png";


const imageDictionary = {
    "Phantom Node": phantomNodeImg,
    "SpectraFlex Adaptive Wear": spectraFlexImg,
    "Morpho-Touch Glove": morphoGloveImg,
    "MissionScope Planner": missionScopeImg,
    "Swarm Intelligence Deployment Suite": swarmImg,
    "Advanced Tactical Mobility System": advTactMobileImg,
    "STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB)": stingrayImg,
    "Cyber-Physical Disruption Suite (CPDS)": disruptionImg,
    "Strategic Effects Orchestration Platform (SEOP)": seopImg,
    "Adaptive Ground Sensing & Fusion System (AGS-FS)": agsfsImg,
    "Tactical Geospatial & Threat Intelligence Hub (TG-TIH)": tgtihImg,
    "Intelligent Information Distribution Engine (IIDE)": iideImg,
    "Secure Device & Data Concealment Module (SDDCM)": sddcmImg
}

const CAPABILITIES = [
    {
        "id": "1",
        "label": "Adaptive Camouflage Systems"
    },
    {
        "id": "2",
        "label": "Environmental Image Projection"
    },
    {
        "id": "3",
        "label": "AI-Powered Urban Terrain Analysis"
    },
    {
        "id": "4",
        "label": "Smart City Infrastructure Interoperability"
    },
    {
        "id": "5",
        "label": "Microbiome-Aided Signature Control"
    },
    {
        "id": "6",
        "label": "Bio-Nanotech Enhanced Stealth Fabrics"
    },
    {
        "id": "7",
        "label": "Dynamic Persona Generation (EMS/Behavioral)"
    },
    {
        "id": "8",
        "label": "Bioremediation-Based Operational Support"
    },
    {
        "id": "9",
        "label": "Autonomous Disease Surveillance Networks"
    }
];

function GapsPage() {
    const [checkedItems, setCheckedItems] = useState({
        focusOne: true,
        focusTwo: true,
        focusThree: true,
        focusFour: true,
        focusFive: true,
        focusSix: true,
    });

    const [capabilityChecks, setCapabilityChecks] = useState(
        CAPABILITIES.reduce((acc, capability) => ({
            ...acc,
            [capability.id]: false
        }), {})
    );

    const [minPercentage, setMinPercentage] = useState(70);

    const allChecked = Object.values(checkedItems).every(Boolean);
    const someChecked = Object.values(checkedItems).some(Boolean);
    const anyCapabilityChecked = Object.values(capabilityChecks).some(Boolean);

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        setCheckedItems({
            focusOne: isChecked,
            focusTwo: isChecked,
            focusThree: isChecked,
            focusFour: isChecked,
            focusFive: isChecked,
            focusSix: isChecked
        })
    }

    const handleCheckboxChange = (event) => {
        const {name, checked} = event.target;
        setCheckedItems((prev) => ({...prev, [name]: checked}))
    }

    const handleCapabilityChange = (event) => {
        const {name, checked} = event.target;
        setCapabilityChecks((prev) => ({...prev, [name]: checked}))
    }

    const handleSliderChange = (newValue) => {
        setMinPercentage(newValue);
    }

    const getFilteredAssets = () => {
        return AssetData.filter(asset => {
            switch(asset.focus_area) {
                case 1: return checkedItems.focusOne;
                case 2: return checkedItems.focusTwo;
                case 3: return checkedItems.focusThree;
                case 4: return checkedItems.focusFour;
                case 5: return checkedItems.focusFive;
                case 6: return checkedItems.focusSix;
                default: return false;
            }
        });
    };

    const filteredAssets = useMemo(() => {
        // If no capabilities are selected, show all assets
        if (!anyCapabilityChecked) return getFilteredAssets();

        return getFilteredAssets().filter(asset => {
            // Get the selected capabilities
            const selectedCapabilities = Object.entries(capabilityChecks)
                .filter(([_, checked]) => checked)
                .map(([id]) => {
                    // Find the matching capability in CAPABILITIES array
                    const capability = CAPABILITIES.find(cap => cap.id === id);
                    return capability.label;
                });

            // For each selected capability, check if the asset meets the minimum percentage
            return selectedCapabilities.every(capabilityLabel => {
                const capability = asset.capabilities.find(cap => 
                    cap.main === capabilityLabel
                );

                // If the capability exists and meets the minimum percentage, include the asset
                return capability && capability.value >= minPercentage;
            });
        });
    }, [capabilityChecks, minPercentage, anyCapabilityChecked, getFilteredAssets]);
    
    return (
        <DashboardLayout>
            <DefaultNavbar />
            <MDBox>
                <Paper>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore/>}>
                            <MDTypography variant="h5">Focus Area</MDTypography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            indeterminate={someChecked && !allChecked}
                                            checked={allChecked}
                                            onChange={handleSelectAll}
                                            sx={{
                                                color: '#2B5587',
                                                '&.Mui-checked': {
                                                    color: '#2B5587',
                                                },
                                            }}
                                        />
                                    }
                                    label="Select All"
                                />
                                <FormControlLabel 
                                    control={<Checkbox name="focusOne" checked={checkedItems.focusOne} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 1: Signature Management"/>
                                <FormControlLabel 
                                    control={<Checkbox name="focusTwo" checked={checkedItems.focusTwo} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 2: Contested Communications"/>
                                <FormControlLabel 
                                    control={<Checkbox name="focusThree" checked={checkedItems.focusThree} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 3: Mobility Applications"/>
                                <FormControlLabel 
                                    control={<Checkbox name="focusFour" checked={checkedItems.focusFour} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 4: Non-Traditional Methods"/>
                                <FormControlLabel 
                                    control={<Checkbox name="focusFive" checked={checkedItems.focusFive} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 5: Sense and Understand"/>
                                <FormControlLabel 
                                    control={<Checkbox name="focusSix" checked={checkedItems.focusSix} onChange={handleCheckboxChange}/>} 
                                    label="Focus Area 6: Ubiquitous Access Comms"/>
                            </FormGroup>
                        </AccordionDetails>
                    </Accordion>
                </Paper>
                <Paper sx={{ mt: 2, p: 2 }}>
                    <MDTypography variant="h5" sx={{ mb: 2 }}>Capability Requirements</MDTypography>
                    <Grid container spacing={2}>
                        {CAPABILITIES.map((capability) => (
                            <Grid item xs={6} md={4} key={capability.id}>
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            name={capability.id} 
                                            checked={capabilityChecks[capability.id]} 
                                            onChange={handleCapabilityChange}
                                        />
                                    } 
                                    label={capability.label}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    
                    <Box sx={{ mt: 3 }}>
                        <GapSlider
                            value={minPercentage}
                            onChange={handleSliderChange}
                            min={0}
                            max={100}
                            step={1}
                            disabled={!anyCapabilityChecked}
                            label="Minimum Capability Coverage"
                            marks={[
                                { value: 0, label: '0%' },
                                { value: 25, label: '25%' },
                                { value: 50, label: '50%' },
                                { value: 75, label: '75%' },
                                { value: 100, label: '100%' }
                            ]}
                        />
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                            Matching Assets ({filteredAssets.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                            {filteredAssets.map((asset, index) => (
                                <Grid item xs={6} md={6} lg={3} key={index}>
                                    <AssetCard
                                        image={asset.image}
                                        title={asset.title}
                                        description={asset.description}
                                        specs={asset.specs}
                                        capabilities={asset.capabilities}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Paper>
            </MDBox>
        </DashboardLayout>
    );
}

export default GapsPage;